import { AnyAri } from '@atlassian/ari/any-ari';
import type { SearchCombinedUnitMetadata } from '@atlassian/search-client/config';

import { getSiteCloudId } from '../../constants/filters/site';
import { is1pProductKey, type ProductKeys } from '../../constants/product-keys';
import type { QueryParams } from '../query-params/types';

/** Extracts the cloudId from a result ARI; returns undefined for blank/malformed/non-ARI ids. */
export const extractCloudIdFromAri = (ari: string | null | undefined): string | undefined => {
	if (!ari) {
		return undefined;
	}

	try {
		return AnyAri.parse(ari).cloudId?.trim() || undefined;
	} catch {
		return undefined;
	}
};

/**
 * Normalises a raw `?sites=` query param into site ids. The value reaches us unparsed, so it can be
 * a comma-separated string or an already-split array.
 */
const toSiteIds = (rawSelectedSites: QueryParams[string]): string[] => {
	const sites = Array.isArray(rawSelectedSites)
		? rawSelectedSites
		: typeof rawSelectedSites === 'string'
			? rawSelectedSites.split(',')
			: [];

	return sites.map((site) => site.trim()).filter(Boolean);
};

type SiteScope = ReadonlyMap<string, string> & {
	/** How many sites are available to the user, when unit metadata has loaded. */
	readonly availableSitesCount?: number;
};

/**
 * Builds the cloudId -> siteName lookup for the sites currently in scope: the sites selected via
 * `?sites=`, plus the current site. Ids are resolved against `unitMetadata`, so unknown or stale
 * ids are dropped.
 */
export const buildSelectedSites = (
	rawSelectedSites: QueryParams[string],
	unitMetadata: readonly SearchCombinedUnitMetadata[] | undefined,
	currentCloudId: string | undefined,
): SiteScope => {
	const selectedSites = Object.assign(new Map<string, string>(), {
		availableSitesCount: unitMetadata?.length,
	});
	const sitesInScope = new Set(toSiteIds(rawSelectedSites));

	if (currentCloudId) {
		sitesInScope.add(currentCloudId);
	}

	for (const site of unitMetadata ?? []) {
		const siteCloudId = getSiteCloudId(site.id);

		if (sitesInScope.has(siteCloudId) && !selectedSites.has(siteCloudId)) {
			selectedSites.set(siteCloudId, site.siteName);
		}
	}

	return selectedSites;
};

/** Returns how many sites are available to the user when unit metadata has loaded. */
export const getAvailableSitesCount = (selectedSites: SiteScope | undefined): number | undefined =>
	selectedSites?.availableSitesCount;

export const getAvailableProductsForSelectedCloudIds = ({
	availableProducts,
	selectedCloudIds,
	unitMetadata,
}: {
	availableProducts: ProductKeys[];
	selectedCloudIds: readonly string[] | undefined;
	unitMetadata: readonly SearchCombinedUnitMetadata[] | undefined;
}): ProductKeys[] => {
	const uniqueSelectedCloudIds = Array.from(new Set(selectedCloudIds?.filter(Boolean)));

	if (uniqueSelectedCloudIds.length <= 1) {
		return availableProducts;
	}

	const selectedCloudIdSet = new Set(uniqueSelectedCloudIds);
	const selectedSiteProductSet = new Set<ProductKeys>();
	let matchedSiteCount = 0;

	for (const site of unitMetadata ?? []) {
		if (!selectedCloudIdSet.has(getSiteCloudId(site.id))) {
			continue;
		}

		matchedSiteCount += 1;

		if (!site.firstPartyProducts) {
			return availableProducts;
		}

		for (const product of site.firstPartyProducts) {
			if (is1pProductKey(product.name)) {
				selectedSiteProductSet.add(product.name);
			}
		}
	}

	const selectedSiteProducts = Array.from(selectedSiteProductSet);

	if (matchedSiteCount <= 1 || !selectedSiteProducts.length) {
		return availableProducts;
	}

	return Array.from(
		new Set([
			...selectedSiteProducts,
			...availableProducts.filter((product) => !is1pProductKey(product)),
		]),
	);
};

/** Resolves a result's owning-site name from the prebuilt cloudId -> siteName lookup. */
export const getSiteName = (
	resultId: string | null | undefined,
	selectedSites: ReadonlyMap<string, string> | undefined,
): string | undefined => {
	const cloudId = extractCloudIdFromAri(resultId);

	return cloudId ? selectedSites?.get(cloudId) : undefined;
};

/** Site attribution for a single result, spread into result interaction metadata. */
export type SearchResultSiteMetadata = {
	/** How many sites the search covered. */
	selectedSitesCount?: number;
	/** Whether the result belongs to the current site. */
	isPrimarySite?: boolean;
	/** How many sites are available to the user. */
	availableSitesCount?: number;
};

/**
 * Site attribution for a result, derived from the `selectedSites` lookup the renderers already
 * receive.
 *
 * When the search is not site-scoped, returns only the available-site count when known. Leaves
 * `isPrimarySite` undefined when either cloudId is unknown, so unattributable results are not
 * reported as belonging to another site.
 */
export const getSiteMetadata = (
	resultId: string | null | undefined,
	currentCloudId: string | undefined,
	selectedSites: SiteScope | undefined,
): SearchResultSiteMetadata => {
	const availableSitesCount = getAvailableSitesCount(selectedSites);

	// An absent or empty lookup means the search was not site-scoped. Availability is independent of
	// the search scope, so report it when known.
	if (!selectedSites?.size) {
		return availableSitesCount !== undefined ? { availableSitesCount } : {};
	}

	const resultCloudId = extractCloudIdFromAri(resultId);

	return {
		selectedSitesCount: selectedSites.size,
		isPrimarySite: currentCloudId && resultCloudId ? resultCloudId === currentCloudId : undefined,
		...(availableSitesCount !== undefined ? { availableSitesCount } : {}),
	};
};
